import React, { useEffect, useMemo, useState } from "react";

import { getReglements } from "../../../../../services/achatsServices";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  FaClipboard,
  FaCheck,
  FaRedo,
  FaSearch,
  FaShoppingBag,
  FaSortAmountDownAlt,
} from "react-icons/fa";

const CURRENCY = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const COLORS = [
  "#6366F1",
  "#22C55E",
  "#F59E0B",
  "#EF4444",
  "#06B6D4",
  "#A855F7",
  "#14B8A6",
  "#F97316",
  "#EC4899",
  "#64748B",
];

function isAcompteText(value = "") {
  return /acompte|arrhes|d[ée]p[ôo]t|deposit|advance/i.test(String(value || ""));
}

function toAmount(value) {
  const number = Number.parseFloat(value || 0);
  return Number.isFinite(number) ? number : 0;
}

function formatCurrency(value) {
  return CURRENCY.format(Number(value || 0));
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("fr-FR");
}

function seasonBounds(season) {
  const [startYear, endYear] = String(season || "").split("-").map(Number);

  if (!startYear || !endYear) {
    return {
      start: null,
      end: null,
    };
  }

  return {
    // Saison sportive classique : 1 août -> 31 juillet
    start: new Date(`${startYear}-08-01T00:00:00`),
    end: new Date(`${endYear}-07-31T23:59:59`),
  };
}

function isDateInSeason(dateValue, season) {
  const { start, end } = seasonBounds(season);

  if (!start || !end || !dateValue) return false;

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return false;

  return date >= start && date <= end;
}

function extractArray(data) {
  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.reglements)) return data.reglements;
  }

  return [];
}

function copyTextFallback(text) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";

  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

function normalizeRows(achats, selectedSeason) {
  const filtered = extractArray(achats).filter((achat) => {
    if (!achat?.Article) return false;

    if (!isDateInSeason(achat?.date_achat, selectedSeason)) return false;

    const articleName = achat?.Article?.nom || "";
    const description = achat?.Article?.description || "";
    const message = achat?.message_achat || "";

    if (
      isAcompteText(articleName) ||
      isAcompteText(description) ||
      isAcompteText(message)
    ) {
      return false;
    }

    return true;
  });

  const map = new Map();

  for (const achat of filtered) {
    const article = achat?.Article?.nom || "Article";
    const description = achat?.Article?.description || "";
    const quantite = Number(achat?.quantite || 0);
    const prixTotal = toAmount(achat?.prix_total);

    if (!map.has(article)) {
      map.set(article, {
        article,
        description,
        quantite: 0,
        ca: 0,
        ventes: 0,
      });
    }

    const current = map.get(article);
    current.quantite += quantite;
    current.ca += prixTotal;
    current.ventes += 1;
  }

  return Array.from(map.values()).sort((a, b) => b.ca - a.ca);
}

function MiniStat({ label, value, helper, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
    violet:
      "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-500/20 dark:bg-violet-500/10 dark:text-violet-300",
  };

  return (
    <div className={`rounded-2xl border px-4 py-3 ${accents[accent]}`}>
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
        {label}
      </p>

      <p className="mt-0.5 text-xl font-bold text-slate-950 dark:text-white">
        {value}
      </p>

      {helper ? (
        <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
          {helper}
        </p>
      ) : null}
    </div>
  );
}

function ChartTooltip({ active, payload, label, mode = "currency" }) {
  if (!active || !payload?.length) return null;

  const value = payload[0]?.value || 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 text-sm shadow-xl dark:border-slate-700 dark:bg-slate-900">
      <p className="font-bold text-slate-900 dark:text-white">{label}</p>

      <p className="mt-2 text-slate-700 dark:text-slate-300">
        {mode === "currency" ? "Montant" : "Quantité"} :{" "}
        <span className="font-bold">
          {mode === "currency" ? formatCurrency(value) : formatNumber(value)}
        </span>
      </p>
    </div>
  );
}

function DonutTooltip({ active, payload, total }) {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload;
  const value = Number(row?.ca || 0);
  const percent = total ? Math.round((value / total) * 1000) / 10 : 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 text-sm shadow-xl dark:border-slate-700 dark:bg-slate-900">
      <p className="font-bold text-slate-900 dark:text-white">{row?.article}</p>

      <p className="mt-2 text-slate-700 dark:text-slate-300">
        CA : <span className="font-bold">{formatCurrency(value)}</span>
      </p>

      <p className="mt-1 rounded-xl bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
        {percent}% du total affiché
      </p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="h-5 w-72 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
          <div className="mt-2 h-3 w-96 max-w-full animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
        </div>

        <div className="h-10 w-40 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[...Array(4)].map((_, index) => (
          <div
            key={index}
            className="h-20 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800"
          />
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="h-[320px] animate-pulse rounded-[24px] bg-slate-200 dark:bg-slate-800" />
        <div className="h-[320px] animate-pulse rounded-[24px] bg-slate-200 dark:bg-slate-800" />
      </div>
    </div>
  );
}

function ErrorPanel({ message, onRetry }) {
  return (
    <div className="rounded-[24px] border border-rose-200 bg-rose-50 p-5 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
      <p className="font-bold">Erreur de chargement</p>

      <p className="mt-1 text-sm leading-6">{message}</p>

      <button
        type="button"
        onClick={onRetry}
        className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-rose-700"
      >
        <FaRedo />
        Réessayer
      </button>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-950/50">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
        <FaShoppingBag />
      </div>

      <h3 className="mt-4 text-lg font-bold text-slate-900 dark:text-white">
        Aucune vente boutique
      </h3>

      <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
        Aucun achat boutique n’a été trouvé pour cette saison.
      </p>
    </div>
  );
}

export default function BoutiqueSalesPanel({ selectedSeason }) {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [sortMode, setSortMode] = useState("ca-desc");
  const [limitMode, setLimitMode] = useState("top10");

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [copied, setCopied] = useState(false);

  const fetchData = async () => {
    if (!selectedSeason) return;

    try {
      setLoading(true);
      setErrorMsg("");

      const response = await getReglements();
      const normalized = normalizeRows(response, selectedSeason);

      setRows(normalized);
    } catch (error) {
      console.error("Erreur chargement achats boutique :", error);
      setRows([]);
      setErrorMsg("Impossible de récupérer les achats boutique de la saison.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedSeason]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();

    let output = q
      ? rows.filter((row) => {
          return (
            row.article.toLowerCase().includes(q) ||
            row.description.toLowerCase().includes(q)
          );
        })
      : [...rows];

    output.sort((a, b) => {
      if (sortMode === "qty-desc") return b.quantite - a.quantite;
      if (sortMode === "name") return a.article.localeCompare(b.article, "fr");
      return b.ca - a.ca;
    });

    return output;
  }, [rows, query, sortMode]);

  const displayedRows = useMemo(() => {
    if (limitMode === "all") return filteredRows;
    return filteredRows.slice(0, 10);
  }, [filteredRows, limitMode]);

  const kpis = useMemo(() => {
    const ca = filteredRows.reduce((sum, row) => sum + Number(row.ca || 0), 0);
    const quantite = filteredRows.reduce(
      (sum, row) => sum + Number(row.quantite || 0),
      0
    );
    const ventes = filteredRows.reduce(
      (sum, row) => sum + Number(row.ventes || 0),
      0
    );
    const panierMoyen = ventes ? ca / ventes : 0;

    return {
      articles: filteredRows.length,
      quantite,
      ca,
      ventes,
      panierMoyen,
    };
  }, [filteredRows]);

  const chartRows = useMemo(() => {
    return displayedRows.map((row) => ({
      ...row,
      label:
        row.article.length > 18 ? `${row.article.slice(0, 18)}…` : row.article,
    }));
  }, [displayedRows]);

  const copyTableToClipboard = async () => {
    const header = ["Article", "Description", "Quantité", "Ventes", "CA"];

    const lines = filteredRows.map((row) => [
      row.article,
      row.description || "",
      row.quantite,
      row.ventes,
      row.ca.toFixed(2).replace(".", ","),
    ]);

    const totalRow = [
      "Total",
      "",
      kpis.quantite,
      kpis.ventes,
      kpis.ca.toFixed(2).replace(".", ","),
    ];

    const text = [header, ...lines, totalRow]
      .map((line) => line.join("\t"))
      .join("\n");

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        copyTextFallback(text);
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (error) {
      console.error("Erreur copie tableau boutique :", error);
      copyTextFallback(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }
  };

  if (loading) return <LoadingSkeleton />;

  if (errorMsg) {
    return <ErrorPanel message={errorMsg} onRetry={fetchData} />;
  }

  if (!rows.length) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                <FaShoppingBag />
              </span>

              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                  Achats boutique
                </h3>

                <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Saison {selectedSeason || "—"}
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={fetchData}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaRedo />
            Actualiser
          </button>
        </div>

        <EmptyState />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
              <FaShoppingBag />
            </span>

            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Achats boutique
              </h3>

              <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                Analyse des ventes par article pour la saison{" "}
                <span className="font-bold">{selectedSeason || "—"}</span>,
                hors acomptes.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={copyTableToClipboard}
            disabled={!filteredRows.length}
            className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-bold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${
              copied
                ? "bg-emerald-600 text-white"
                : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            }`}
          >
            {copied ? <FaCheck /> : <FaClipboard />}
            {copied ? "Copié" : "Copier"}
          </button>

          <button
            type="button"
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaRedo className={loading ? "animate-spin" : ""} />
            Actualiser
          </button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MiniStat
          label="Articles"
          value={formatNumber(kpis.articles)}
          helper="Articles distincts"
          accent="indigo"
        />

        <MiniStat
          label="Quantités"
          value={formatNumber(kpis.quantite)}
          helper="Articles vendus"
          accent="emerald"
        />

        <MiniStat
          label="Ventes"
          value={formatNumber(kpis.ventes)}
          helper="Lignes d’achat"
          accent="amber"
        />

        <MiniStat
          label="CA boutique"
          value={formatCurrency(kpis.ca)}
          helper={`Panier moyen : ${formatCurrency(kpis.panierMoyen)}`}
          accent="violet"
        />
      </div>

      <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/50">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="relative w-full xl:max-w-md">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Rechercher un article..."
              className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <select
              value={sortMode}
              onChange={(event) => setSortMode(event.target.value)}
              className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="ca-desc">Tri par chiffre d’affaires</option>
              <option value="qty-desc">Tri par quantité</option>
              <option value="name">Tri alphabétique</option>
            </select>

            <button
              type="button"
              onClick={() =>
                setLimitMode((current) => (current === "top10" ? "all" : "top10"))
              }
              className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-bold transition ${
                limitMode === "top10"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              }`}
            >
              <FaSortAmountDownAlt />
              {limitMode === "top10" ? "Top 10" : "Tout afficher"}
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/60">
          <div className="mb-3">
            <h4 className="text-base font-bold text-slate-900 dark:text-white">
              Quantités vendues
            </h4>
            <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
              Nombre d’articles vendus par référence.
            </p>
          </div>

          <div className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartRows}
                margin={{ top: 12, right: 12, left: -10, bottom: 46 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(100,116,139,0.25)"
                />

                <XAxis
                  dataKey="label"
                  interval={0}
                  angle={-25}
                  textAnchor="end"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                />

                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 12, fill: "#64748b" }}
                />

                <Tooltip content={<ChartTooltip mode="number" />} />

                <Bar
                  dataKey="quantite"
                  name="Quantité"
                  fill="#6366F1"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/60">
          <div className="mb-3">
            <h4 className="text-base font-bold text-slate-900 dark:text-white">
              Répartition du CA
            </h4>
            <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
              Part de chaque article dans le chiffre d’affaires boutique.
            </p>
          </div>

          <div className="grid gap-3 lg:grid-cols-[220px_1fr]">
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartRows}
                    dataKey="ca"
                    nameKey="article"
                    cx="50%"
                    cy="50%"
                    innerRadius="58%"
                    outerRadius="82%"
                    paddingAngle={2}
                  >
                    {chartRows.map((entry, index) => (
                      <Cell
                        key={entry.article}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>

                  <Tooltip total={kpis.ca} content={<DonutTooltip total={kpis.ca} />} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="max-h-[280px] space-y-2 overflow-y-auto pr-1">
              {chartRows.map((row, index) => {
                const percent = kpis.ca
                  ? Math.round((row.ca / kpis.ca) * 1000) / 10
                  : 0;

                return (
                  <div
                    key={row.article}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-900"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="h-3 w-3 shrink-0 rounded-full"
                        style={{ background: COLORS[index % COLORS.length] }}
                      />

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                            {row.article}
                          </p>

                          <p className="shrink-0 text-sm font-bold text-slate-900 dark:text-white">
                            {formatCurrency(row.ca)}
                          </p>
                        </div>

                        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${percent}%`,
                              background: COLORS[index % COLORS.length],
                            }}
                          />
                        </div>
                      </div>

                      <span className="w-12 shrink-0 text-right text-xs font-bold text-slate-500 dark:text-slate-400">
                        {percent}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-[24px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/60">
        <table className="min-w-[820px] w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900">
            <tr className="text-left">
              <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                Article
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                Description
              </th>

              <th className="px-4 py-3 text-right font-bold text-slate-700 dark:text-slate-200">
                Quantité
              </th>

              <th className="px-4 py-3 text-right font-bold text-slate-700 dark:text-slate-200">
                Ventes
              </th>

              <th className="px-4 py-3 text-right font-bold text-slate-700 dark:text-slate-200">
                CA
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {filteredRows.map((row) => (
              <tr
                key={row.article}
                className="transition hover:bg-slate-50 dark:hover:bg-slate-900"
              >
                <td className="sticky left-0 z-10 bg-white px-4 py-3 font-bold text-slate-900 dark:bg-slate-950 dark:text-white">
                  {row.article}
                </td>

                <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                  {row.description || "—"}
                </td>

                <td className="px-4 py-3 text-right text-slate-700 dark:text-slate-300">
                  {formatNumber(row.quantite)}
                </td>

                <td className="px-4 py-3 text-right text-slate-700 dark:text-slate-300">
                  {formatNumber(row.ventes)}
                </td>

                <td className="px-4 py-3 text-right font-bold text-slate-900 dark:text-white">
                  {formatCurrency(row.ca)}
                </td>
              </tr>
            ))}

            <tr className="bg-slate-50 dark:bg-slate-900">
              <td className="sticky left-0 z-10 bg-slate-50 px-4 py-3 font-bold text-slate-900 dark:bg-slate-900 dark:text-white">
                Total
              </td>

              <td className="px-4 py-3" />

              <td className="px-4 py-3 text-right font-bold text-slate-900 dark:text-white">
                {formatNumber(kpis.quantite)}
              </td>

              <td className="px-4 py-3 text-right font-bold text-slate-900 dark:text-white">
                {formatNumber(kpis.ventes)}
              </td>

              <td className="px-4 py-3 text-right font-bold text-slate-900 dark:text-white">
                {formatCurrency(kpis.ca)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
        Les ventes contenant “acompte”, “arrhes”, “deposit” ou “advance” sont
        exclues afin de conserver uniquement les achats boutique.
      </p>
    </div>
  );
}